import { Link, useSearchParams } from 'react-router-dom';
import { CalendarDays, List, Map as MapIcon, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { JOBS_VIEW_PATH, useJobsViews, useRememberJobsView, type JobsView } from './jobsViews';

const VIEW_META: Record<JobsView, { label: string; icon: LucideIcon }> = {
  list: { label: 'List', icon: List },
  schedule: { label: 'Schedule', icon: CalendarDays },
  map: { label: 'Map', icon: MapIcon },
};

interface JobsHeaderProps {
  view: JobsView;
  description: React.ReactNode;
}

/**
 * The one header for every Jobs view: title, a line about the view, and the
 * List | Schedule | Map switch. Schedule and Map share the day they're looking at.
 */
export function JobsHeader({ view, description }: JobsHeaderProps) {
  useRememberJobsView(view);
  const { views, toBookCount } = useJobsViews();
  const [params] = useSearchParams();
  const date = params.get('date');

  const hrefFor = (target: JobsView) => {
    const path = JOBS_VIEW_PATH[target];
    return target !== 'list' && date ? `${path}?date=${encodeURIComponent(date)}` : path;
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Jobs</h1>

        <nav className="flex h-9 items-center rounded-md border p-0.5" aria-label="Jobs view">
          {views.map((v) => {
            const { label, icon: Icon } = VIEW_META[v];
            const active = v === view;
            const showCount = v === 'schedule' && toBookCount > 0;
            return (
              <Link
                key={v}
                to={hrefFor(v)}
                aria-current={active ? 'page' : undefined}
                title={showCount ? `${toBookCount} accepted quote${toBookCount === 1 ? '' : 's'} to book` : undefined}
                className={cn(
                  'flex h-full items-center gap-1.5 rounded px-2.5 text-[13px] font-medium transition-colors',
                  active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
                {showCount && (
                  <span className="rounded-full bg-success/15 px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums text-success">
                    {toBookCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>
      <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>
    </div>
  );
}
